import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function PeopleImport() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canImport = user && ["super_admin", "hr_admin"].includes(user.role);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [committing, setCommitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef();

  if (!canImport) {
    return (
      <div className="p-6">
        <p className="text-slate-500">Only Super Admin and HR Admin can bulk import employees.</p>
      </div>
    );
  }

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/employees/import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "qrewhr_employees_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const exportAll = async () => {
    try {
      const res = await api.get("/employees/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qrewhr_employees_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Employee CSV exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const doPreview = async (f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    setResult(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/employees/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Parsed ${data.total_rows} rows · ${data.valid_rows} ready`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Parse failed");
    } finally {
      setLoading(false);
    }
  };

  const doCommit = async () => {
    if (!preview) return;
    const readyRows = preview.rows.filter((r) => r.__status === "ready");
    if (readyRows.length === 0) {
      toast.error("No valid rows to import");
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/employees/import/commit", {
        rows: readyRows,
        skip_existing: true,
      });
      setResult(data);
      toast.success(`Created ${data.created_count} · Skipped ${data.skipped_count} · Failed ${data.failed_count}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/people" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-2" data-testid="back-to-people">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to People
          </Link>
          <h1 className="text-2xl font-bold font-[Outfit]">Bulk Import / Export</h1>
          <p className="text-sm text-slate-500 mt-1">Upload CSV or XLSX to onboard many employees at once. Dry-run preview shows errors before commit.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
            <Download className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={exportAll} data-testid="export-all-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export All
          </Button>
        </div>
      </div>

      {!result && (
        <Card data-testid="upload-card">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit]">1. Upload file</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center hover:border-cyan-400 transition">
              <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 mb-3">
                Drop CSV or XLSX here, or click to browse.<br />
                <span className="text-xs text-slate-400">Max 10 MB · 17 columns (download template for exact headers)</span>
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => doPreview(e.target.files?.[0])}
                className="hidden"
                data-testid="import-file-input"
                id="import-file-input"
              />
              <label htmlFor="import-file-input">
                <Button asChild variant="secondary" disabled={loading} data-testid="choose-file-btn">
                  <span>{loading ? "Parsing…" : "Choose file"}</span>
                </Button>
              </label>
              {file && <p className="mt-3 text-xs text-slate-500">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
            </div>
          </CardContent>
        </Card>
      )}

      {preview && !result && (
        <Card data-testid="preview-card">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit] flex items-center gap-2">
              2. Preview (dry run)
              <Badge variant="outline" className="text-xs" data-testid="preview-summary">
                {preview.total_rows} total · {preview.valid_rows} ready · {preview.invalid_rows} invalid · {preview.duplicates} duplicate
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <StatCard label="Total rows" value={preview.total_rows} tone="slate" />
              <StatCard label="Ready to import" value={preview.valid_rows} tone="emerald" data-testid="stat-ready" />
              <StatCard label="Invalid" value={preview.invalid_rows} tone="rose" data-testid="stat-invalid" />
              <StatCard label="Already exists" value={preview.duplicates} tone="amber" data-testid="stat-duplicate" />
            </div>

            <div className="max-h-96 overflow-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Row</th>
                    <th className="text-left p-2">Status</th>
                    <th className="text-left p-2">Full name</th>
                    <th className="text-left p-2">Email</th>
                    <th className="text-left p-2">Role</th>
                    <th className="text-left p-2">Department</th>
                    <th className="text-left p-2">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((r) => {
                    const rowErrs = preview.errors.find((e) => e.row_index === r.__row_index);
                    const tone = r.__status === "ready" ? "text-emerald-600 bg-emerald-50" : r.__status === "duplicate" ? "text-amber-700 bg-amber-50" : "text-rose-600 bg-rose-50";
                    return (
                      <tr key={r.__row_index} className="border-t hover:bg-slate-50" data-testid={`preview-row-${r.__row_index}`}>
                        <td className="p-2 text-slate-400">{r.__row_index}</td>
                        <td className="p-2"><span className={`px-1.5 py-0.5 rounded ${tone} text-[10px] uppercase font-semibold`}>{r.__status}</span></td>
                        <td className="p-2">{r.full_name}</td>
                        <td className="p-2 font-mono">{r.email}</td>
                        <td className="p-2">{r.role || "employee"}</td>
                        <td className="p-2">{r.department_name}</td>
                        <td className="p-2 text-rose-600">{rowErrs ? rowErrs.errors.join(", ") : ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={reset} data-testid="cancel-import-btn">Cancel</Button>
              <Button
                onClick={doCommit}
                disabled={committing || preview.valid_rows === 0}
                className="bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="commit-import-btn"
              >
                {committing ? "Importing…" : `Import ${preview.valid_rows} ready rows`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card data-testid="result-card">
          <CardHeader>
            <CardTitle className="text-base font-[Outfit] flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Import complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <StatCard label="Created" value={result.created_count} tone="emerald" data-testid="result-created" />
              <StatCard label="Skipped (existing)" value={result.skipped_count} tone="amber" />
              <StatCard label="Failed" value={result.failed_count} tone="rose" data-testid="result-failed" />
            </div>
            {result.failed_count > 0 && (
              <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                <p className="text-xs font-semibold text-rose-700 mb-1 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Failures
                </p>
                <ul className="text-xs text-rose-600 space-y-0.5">
                  {result.failed.map((f, i) => <li key={i}>· {f.email} — {f.reason}</li>)}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={reset} data-testid="import-more-btn">Import more</Button>
              <Button onClick={() => nav("/people")} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="goto-people-btn">View People</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "slate", ...rest }) {
  const toneMap = {
    slate: "bg-slate-50 text-slate-700 border-slate-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
  };
  return (
    <div className={`rounded-md border p-3 ${toneMap[tone]}`} {...rest}>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</div>
      <div className="text-2xl font-bold font-[Outfit] mt-0.5">{value}</div>
    </div>
  );
}
